/**
 * /ndt-inspection/reports/:id — one inspection report (GET /reports/:id,
 * fieldiq-api#1152): its header, the API's statistics, one line per covered
 * exam, the reference set (technicians, instruments, sources), and the summary.
 *
 * DRAFT vs ISSUED. A draft's coverage and figures are computed live by the
 * API on every read; issuing numbers it (NDR-NNNN) and freezes all of it. An
 * issued or void report is read-only here — the database refuses changes too.
 *
 * THE AI SUMMARY IS A DRAFT UNTIL A PERSON APPROVES IT. `summaryDraft` is shown
 * only on a draft report, under "AI draft — review before approving", in an
 * editable box. Approve sends the edited text (POST approve-summary); only
 * then is it `summaryFinal`, shown as "Approved summary" with who and when.
 * The PDF prints summaryFinal only. Regenerate queues the AI and returns with
 * the draft cleared, so the page polls until a new draft lands or gives up.
 *
 * Issue and Void confirm INLINE, on the page — no window.confirm, no modal.
 * Every refusal is a sentence from lib/ndt-reports.ts. Nothing is computed:
 * every figure is the API's, in the tenant's units (header.units).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Ban, CheckCircle2, Download, FileText, Layers, Pencil, RefreshCw, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getBlobApiErrorMessage, api } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import {
  ndtPersonName,
  useApproveNdtReportSummary,
  useIssueNdtReport,
  useNdtPeople,
  useNdtReport,
  useRegenerateNdtReportSummary,
  useVoidNdtReport,
} from '../../hooks/useNdtInspection';
import { saveBlobAs } from '../../lib/blob-tab';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { formatNdtActivity, formatNdtValue, formatRepairRate, ndtCycleLabel, ndtExamResultSkin, ndtLevelLabel } from '../../lib/ndt-inspection';
import {
  canWriteNdtReports,
  ndtReportKindLabel,
  ndtReportNumberText,
  ndtReportPdfFilename,
  ndtReportPeriod,
  ndtReportRefusal,
  ndtReportStatusSkin,
  ndtReportWarningText,
  type NdtReportRefusal,
} from '../../lib/ndt-reports';
import type { NdtReport, NdtReportLine, NdtReportWarning } from '../../types/ndt-inspection';
import { useBackToList } from '../downhole/recordPage';
import { isNotFound, NDT_INPUT, NDT_PRIMARY, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtLoading, NdtPanel, NdtRegister, NdtSpec, type NdtColumn } from './NdtBits';
import { NdtWarnings } from './NdtJobBits';
import { NdtEditReportDialog, NdtReportRefusalNotice } from './NdtReportBits';

/** How often the page re-reads while the AI drafts, and when it stops waiting. */
export const NDT_REPORT_POLL_MS = 2500;
export const NDT_REPORT_DRAFT_TIMEOUT_MS = 90_000;
/** A report this new with no draft yet is assumed to be drafting (create enqueues the AI). */
const FRESH_MS = 2 * 60_000;

const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50';

export default function ReportDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList('/ndt-inspection/reports');
  const { user } = useAuth();
  const [draftingSince, setDraftingSince] = useState<number | null>(null);
  const [draftTimedOut, setDraftTimedOut] = useState(false);
  const query = useNdtReport(id, draftingSince !== null ? NDT_REPORT_POLL_MS : false);
  const report = query.data?.data ?? null;
  const meta = query.data?.meta;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const canWrite = canWriteNdtReports(user?.role) && !!report;
  const isDraft = report?.status === 'draft';

  const issue = useIssueNdtReport();
  const voidReport = useVoidNdtReport();
  const [confirm, setConfirm] = useState<'issue' | 'void' | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<NdtReportRefusal | null>(null);
  const [warnings, setWarnings] = useState<NdtReportWarning[]>([]);
  const [editing, setEditing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // A freshly created draft has its AI summary on the way: wait for it.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current || !report) return;
    autoStarted.current = true;
    const created = report.createdAt ? Date.parse(report.createdAt) : NaN;
    if (report.status === 'draft' && !report.summaryDraft && !report.summaryFinal && Number.isFinite(created) && Date.now() - created < FRESH_MS) {
      setDraftingSince(Date.now());
    }
  }, [report]);
  // Stop polling when the draft lands, the report leaves draft, or the wait runs out.
  useEffect(() => {
    if (draftingSince === null || !report) return;
    if (report.summaryDraft || report.status !== 'draft') {
      setDraftingSince(null);
      return;
    }
    const left = NDT_REPORT_DRAFT_TIMEOUT_MS - (Date.now() - draftingSince);
    const t = setTimeout(() => {
      setDraftingSince(null);
      setDraftTimedOut(true);
    }, Math.max(0, left));
    return () => clearTimeout(t);
  }, [draftingSince, report]);

  const doIssue = () => {
    if (!report) return;
    setRefusal(null);
    setWarnings([]);
    issue.mutate(report.id, {
      onSuccess: (res) => {
        setConfirm(null);
        setWarnings(res.warnings);
        toast.success(`${res.data?.reportNumber ?? 'Report'} issued`);
      },
      onError: (err) => {
        setConfirm(null);
        setRefusal(ndtReportRefusal(err, 'The report could not be issued.'));
      },
    });
  };
  const doVoid = () => {
    if (!report) return;
    if (!voidReason.trim()) return setVoidError('Say why the report is void.');
    setVoidError(null);
    setRefusal(null);
    voidReport.mutate(
      { id: report.id, reason: voidReason.trim() },
      {
        onSuccess: () => {
          setConfirm(null);
          setVoidReason('');
          toast.success('Report voided');
        },
        onError: (err) => setVoidError(ndtReportRefusal(err, 'The report could not be voided.').message),
      },
    );
  };
  const downloadPdf = async () => {
    if (!report) return;
    setPdfError(null);
    setPdfBusy(true);
    try {
      const res = await api.ndtInspection.reports.pdf(report.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
      saveBlobAs(blob, ndtReportPdfFilename(report));
    } catch (err) {
      setPdfError(await getBlobApiErrorMessage(err, 'The PDF could not be loaded.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const stats = report?.stats;
  const units = report?.header?.units ?? meta?.units ?? null;
  const tz = report?.header?.timeZone ?? meta?.timeZone ?? null;

  return (
    <div data-testid="ndt-report-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={report ? `NDT inspection · ${ndtReportKindLabel(report)}` : 'NDT inspection · Report'}
        title={report ? ndtReportNumberText(report) : query.isLoading ? 'Report' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          report ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={BAND_SECONDARY} onClick={downloadPdf} disabled={pdfBusy}>
                <Download className="size-4" aria-hidden="true" />
                {pdfBusy ? 'Preparing…' : isDraft ? 'Download preview' : 'Download PDF'}
              </button>
              {canWrite && isDraft && (
                <>
                  <button type="button" className={BAND_SECONDARY} onClick={() => setEditing(true)}>
                    <Pencil className="size-4" aria-hidden="true" />
                    Edit
                  </button>
                  <button type="button" className={NDT_PRIMARY} onClick={() => setConfirm('issue')} disabled={issue.isPending}>
                    <Send className="size-4" aria-hidden="true" />
                    Issue
                  </button>
                </>
              )}
              {canWrite && report.status !== 'void' && (
                <button type="button" className={BAND_SECONDARY} onClick={() => setConfirm('void')}>
                  <Ban className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Layers className="size-4" aria-hidden="true" />, label: 'Welds', sub: stats ? `${stats.examsCovered} exams` : undefined, tone: 'neutral', loading: query.isLoading, value: stats ? stats.weldsCovered : '—' },
          { icon: <RefreshCw className="size-4" aria-hidden="true" />, label: 'Repair rate', sub: stats?.smallSample ? 'Small sample' : undefined, tone: 'neutral', loading: query.isLoading, value: formatRepairRate(stats?.repairRate?.rate) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: report ? ndtReportStatusSkin(report.status).label : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <NdtLoading label="Loading report" />
          ) : notFound || (!failure && !report) ? (
            <NdtPanel eyebrow="Report" title="Report">
              <NdtEmpty>This report was not found. It may have been deleted.</NdtEmpty>
            </NdtPanel>
          ) : failure || !report ? (
            <NdtPanel eyebrow="Report" title="Report">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this report" />
            </NdtPanel>
          ) : (
            <>
              <NdtReportRefusalNotice refusal={refusal} />
              {pdfError && (
                <p role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
                  {pdfError}
                </p>
              )}
              {warnings.length > 0 && <NdtWarnings title="Issued, with warnings" warnings={warnings.map(ndtReportWarningText)} />}

              {confirm === 'issue' && isDraft && (
                <div data-testid="ndt-issue-confirm" className="space-y-3 rounded-fi-panel border border-fi-amber/60 bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-semibold">Issue this report?</p>
                  <p>
                    It gets its NDR number, and what it says — {stats?.examsCovered ?? '—'} exams on {stats?.weldsCovered ?? '—'} welds — is frozen. An issued
                    report can only be voided, not changed.
                    {!report.summaryFinal && ' It has no approved summary, so the PDF will have no Summary section.'}
                    {stats && stats.pendingExcluded > 0 && ` ${stats.pendingExcluded} pending exam${stats.pendingExcluded === 1 ? ' is' : 's are'} not on it.`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={NDT_PRIMARY} onClick={doIssue} disabled={issue.isPending}>
                      {issue.isPending ? 'Issuing…' : 'Issue report'}
                    </button>
                    <button type="button" className={NDT_SECONDARY} onClick={() => setConfirm(null)}>
                      Keep as draft
                    </button>
                  </div>
                </div>
              )}
              {confirm === 'void' && report.status !== 'void' && (
                <div data-testid="ndt-void-confirm" className="space-y-3 rounded-fi-panel border border-fi-signal-red/50 bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-semibold">Void this report?</p>
                  <p>
                    {report.status === 'issued'
                      ? `${report.reportNumber} stays on record, stamped VOID. Its number is not reused.`
                      : 'The draft stays on record, marked void.'}
                  </p>
                  {voidError && <p role="alert" className="font-semibold">{voidError}</p>}
                  <label className="block">
                    <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Reason</span>
                    <textarea aria-label="Reason" className={`${NDT_INPUT} h-20 py-2`} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={NDT_PRIMARY} onClick={doVoid} disabled={voidReport.isPending}>
                      {voidReport.isPending ? 'Voiding…' : 'Void report'}
                    </button>
                    <button type="button" className={NDT_SECONDARY} onClick={() => setConfirm(null)}>
                      Keep it
                    </button>
                  </div>
                </div>
              )}

              <ReportHeaderPanel report={report} tz={tz} />
              <SummaryPanel
                report={report}
                canWrite={canWrite}
                drafting={draftingSince !== null}
                timedOut={draftTimedOut}
                onRegenerated={() => {
                  setDraftTimedOut(false);
                  setDraftingSince(Date.now());
                }}
              />
              <StatsPanels report={report} />
              <LinesPanel report={report} lengthUnit={units?.length ?? null} />
              <ReferencePanels report={report} activityUnit={units?.activity ?? null} />
            </>
          )}
        </div>
      </section>

      {report && canWrite && isDraft && <NdtEditReportDialog open={editing} onClose={() => setEditing(false)} report={report} />}
    </div>
  );
}

function ReportHeaderPanel({ report, tz }: { report: NdtReport; tz: string | null }) {
  const job = report.header?.job;
  return (
    <NdtPanel eyebrow="Report" title={report.title || ndtReportKindLabel(report)} testId="ndt-report-header" actions={<NdtBadge skin={ndtReportStatusSkin(report.status)} value={report.status} />}>
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <NdtSpec label="Report number" value={ndtReportNumberText(report)} mono={!!report.reportNumber} />
        <NdtSpec
          label="Job"
          value={
            <Link to={`/ndt-inspection/jobs/${report.jobId}`} className="underline">
              {report.jobNumber || '—'}
            </Link>
          }
        />
        <NdtSpec label="Customer" value={report.customerName || '—'} />
        <NdtSpec label="Acceptance code" value={report.acceptanceCodeLabel || report.acceptanceCode || '—'} />
        <NdtSpec label="Kind" value={ndtReportKindLabel(report)} />
        <NdtSpec label="Covers" value={ndtReportPeriod(report)} />
        <NdtSpec label="Line" value={job?.lineRef || '—'} />
        <NdtSpec label="Location" value={job?.locationName || '—'} />
        <NdtSpec label="Customer contact" value={[report.customerContactName, report.customerContactEmail].filter(Boolean).join(' · ') || '—'} />
        <NdtSpec label="Customer reference" value={report.externalRef || '—'} />
        <NdtSpec label="Issued" value={report.issuedAt ? `${formatInstant(report.issuedAt, tz) ?? '—'}${report.issuedSignatoryName ? ` · ${report.issuedSignatoryName}` : ''}` : '—'} />
        <NdtSpec label="Exams dated" value={report.stats?.firstExamDay ? `${formatCalendarDate(report.stats.firstExamDay) ?? '—'} – ${formatCalendarDate(report.stats.lastExamDay) ?? '—'}` : '—'} />
      </dl>
      {report.status === 'void' && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
          Void{report.voidedAt ? ` ${formatInstant(report.voidedAt, tz) ?? ''}` : ''}: {report.voidReason || '—'}
        </p>
      )}
      {report.status === 'draft' && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">A draft shows the job as it stands now. Issuing freezes it.</p>
      )}
    </NdtPanel>
  );
}

/**
 * The summary. On a draft: the approved text (if any), the AI draft clearly
 * labelled, editable, with Approve and Regenerate. On an issued / void report:
 * the approved summary only — the AI draft never appears once the report is out.
 */
export function SummaryPanel({
  report,
  canWrite,
  drafting,
  timedOut,
  onRegenerated,
}: {
  report: NdtReport;
  canWrite: boolean;
  drafting: boolean;
  timedOut: boolean;
  onRegenerated: () => void;
}) {
  const approve = useApproveNdtReportSummary();
  const regenerate = useRegenerateNdtReportSummary();
  const people = useNdtPeople(!!report.summaryApprovedBy);
  const [text, setText] = useState(report.summaryDraft ?? '');
  const [edited, setEdited] = useState(false);
  const [refusal, setRefusal] = useState<NdtReportRefusal | null>(null);
  // A new draft replaces the box's text unless the person is mid-edit.
  useEffect(() => {
    if (!edited) setText(report.summaryDraft ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.summaryDraft]);

  const isDraft = report.status === 'draft';
  const approverName = (() => {
    if (!report.summaryApprovedBy) return null;
    const p = (people.data ?? []).find((u) => u.id === report.summaryApprovedBy);
    return p ? ndtPersonName(p) : null;
  })();
  const approvedLine = report.summaryApprovedAt
    ? `Approved ${formatInstant(report.summaryApprovedAt, report.header?.timeZone ?? null) ?? ''}${approverName ? ` by ${approverName}` : ''}`
    : 'Approved';

  const doApprove = () => {
    setRefusal(null);
    const trimmed = text.trim();
    approve.mutate(
      // The edited text when the person changed it (or wrote their own); otherwise the API copies the draft.
      { id: report.id, summary: edited || !report.summaryDraft ? trimmed : undefined },
      {
        onSuccess: () => {
          setEdited(false);
          toast.success('Summary approved');
        },
        onError: (err) => setRefusal(ndtReportRefusal(err, 'The summary could not be approved.')),
      },
    );
  };
  const doRegenerate = () => {
    setRefusal(null);
    setEdited(false);
    regenerate.mutate(report.id, {
      onSuccess: () => onRegenerated(),
      onError: (err) => setRefusal(ndtReportRefusal(err, 'A new draft could not be requested.')),
    });
  };

  return (
    <NdtPanel eyebrow="Summary" title="Report summary" testId="ndt-report-summary">
      <div className="space-y-4 px-4 py-4 text-sm text-fieldiq-text">
        <NdtReportRefusalNotice refusal={refusal} />
        {report.summaryFinal ? (
          <div data-testid="ndt-summary-final">
            <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Approved summary</p>
            <p className="mt-1 whitespace-pre-wrap">{report.summaryFinal}</p>
            <p className="mt-1 text-xs text-fieldiq-muted">{approvedLine}</p>
          </div>
        ) : (
          <p data-testid="ndt-summary-none" className="text-fieldiq-muted">
            {isDraft ? 'No summary is approved yet.' : 'This report was issued without a summary.'}
          </p>
        )}

        {isDraft && (
          <div data-testid="ndt-summary-ai" className="space-y-2 border-t border-fieldiq-border pt-4">
            <p className="flex items-center gap-1.5 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <Sparkles className="size-3.5" aria-hidden="true" />
              {report.summaryDraft ? 'AI draft — review before approving' : 'Write the summary'}
            </p>
            {report.summaryDraft && report.summaryDraftStale && (
              <p data-testid="ndt-summary-stale" className="rounded-fi-control border border-fi-signal-amber/50 bg-fi-signal-amber/10 px-3 py-2">
                The report's figures have changed since this draft was written. Regenerate it, or check it against the figures below.
              </p>
            )}
            {drafting && !report.summaryDraft ? (
              <p data-testid="ndt-summary-drafting" className="text-fieldiq-muted" role="status">
                Drafting…
              </p>
            ) : (
              <>
                {!report.summaryDraft && (
                  <p className="text-fieldiq-muted">
                    {timedOut
                      ? 'No AI draft arrived. Write the summary yourself, or try Regenerate again.'
                      : 'There is no AI draft. Write the summary yourself, or ask for a draft.'}
                  </p>
                )}
                {canWrite && (
                  <textarea
                    aria-label="Summary text"
                    className={`${NDT_INPUT} h-40 py-2`}
                    value={text}
                    onChange={(e) => {
                      setText(e.target.value);
                      setEdited(true);
                    }}
                  />
                )}
                {!canWrite && report.summaryDraft && <p className="whitespace-pre-wrap">{report.summaryDraft}</p>}
              </>
            )}
            {canWrite && (
              <div className="flex flex-wrap gap-2">
                <button type="button" className={NDT_PRIMARY} onClick={doApprove} disabled={approve.isPending || !text.trim() || (drafting && !report.summaryDraft)}>
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  {approve.isPending ? 'Approving…' : 'Approve summary'}
                </button>
                <button type="button" className={NDT_SECONDARY} onClick={doRegenerate} disabled={regenerate.isPending || drafting}>
                  <RefreshCw className="size-4" aria-hidden="true" />
                  Regenerate
                </button>
              </div>
            )}
            <p className="text-xs text-fieldiq-muted">Nothing reaches the PDF until a person approves it.</p>
          </div>
        )}
      </div>
    </NdtPanel>
  );
}

function Count({ label, value }: { label: string; value: number | null | undefined }) {
  return <NdtSpec label={label} value={typeof value === 'number' ? value : '—'} mono />;
}

function StatsPanels({ report }: { report: NdtReport }) {
  const s = report.stats;
  if (!s) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="ndt-report-stats">
      <NdtPanel eyebrow="Results" title="Exams by result">
        <dl className="grid grid-cols-3 gap-4 px-4 py-4">
          <Count label="Accepted" value={s.results?.accept} />
          <Count label="Repair" value={s.results?.repair} />
          <Count label="Cut out" value={s.results?.cutout} />
          <Count label="Original" value={s.cycles?.original} />
          <Count label="R1" value={s.cycles?.R1} />
          <Count label="R2" value={s.cycles?.R2} />
        </dl>
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
          {s.weldsCovered} weld{s.weldsCovered === 1 ? '' : 's'}, {s.examsCovered} exam{s.examsCovered === 1 ? '' : 's'}.
          {s.pendingExcluded > 0 && ` ${s.pendingExcluded} pending exam${s.pendingExcluded === 1 ? ' has' : 's have'} no result and ${s.pendingExcluded === 1 ? 'is' : 'are'} not listed.`}
        </p>
      </NdtPanel>
      <NdtPanel eyebrow="Methods" title="By method">
        {s.byMethod?.length ? (
          <ul className="divide-y divide-fieldiq-border">
            {s.byMethod.map((m) => (
              <li key={m.method} className="flex items-center justify-between gap-3 px-4 py-2 text-sm text-fieldiq-text">
                <span>{m.methodLabel || m.method}</span>
                <span className="font-mono tabular-nums text-xs">
                  {m.exams} exams · {m.accept} acc · {m.repair} rep · {m.cutout} cut
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <NdtEmpty>No exams are covered.</NdtEmpty>
        )}
      </NdtPanel>
      <NdtPanel eyebrow="Quality" title="Repair rate and indications">
        <dl className="grid grid-cols-2 gap-4 px-4 py-4">
          <NdtSpec label="Repair rate" value={formatRepairRate(s.repairRate?.rate)} mono />
          <NdtSpec label="Welds repaired" value={s.repairRate ? `${s.repairRate.repaired} of ${s.repairRate.examined} repaired` : '—'} />
          <Count label="Indications" value={s.indications?.total} />
          <Count label="Rejectable" value={s.indications?.rejectable} />
        </dl>
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
          {s.smallSample ? 'Small sample — fewer than 5 welds examined, so the rate says little. ' : ''}
          {s.indications?.byType?.length ? s.indications.byType.map((t) => `${t.label || t.type} ${t.count}`).join(' · ') : 'No indications recorded.'}
        </p>
      </NdtPanel>
    </div>
  );
}

function LinesPanel({ report, lengthUnit }: { report: NdtReport; lengthUnit: string | null }) {
  const lines = report.lines ?? [];
  const columns: NdtColumn<NdtReportLine & { id: string }>[] = [
    { key: 'weld', header: 'Weld', render: (l) => <span className="font-mono font-extrabold text-white">{l.weldNumber || '—'}</span> },
    { key: 'line', header: 'Line / station', render: (l) => [l.lineRef, l.station].filter(Boolean).join(' · ') || '—' },
    { key: 'size', header: lengthUnit ? `Dia × wall (${lengthUnit})` : 'Dia × wall', render: (l) => `${formatNdtValue(l.diameter, null)} × ${formatNdtValue(l.wall, null)}` },
    { key: 'method', header: 'Method', render: (l) => [l.methodLabel || l.method, l.techniqueLabel].filter(Boolean).join(' · ') || '—' },
    { key: 'cycle', header: 'Cycle', render: (l) => ndtCycleLabel(l.cycle) },
    { key: 'result', header: 'Result', render: (l) => <NdtBadge skin={ndtExamResultSkin(l.result)} value={l.result} /> },
    {
      key: 'ind',
      header: 'Indications',
      render: (l) =>
        l.indications.length === 0
          ? 'None'
          : l.indications
              .map((i) => `${i.indicationTypeLabel || i.indicationType}${i.length !== null ? ` ${formatNdtValue(i.length, lengthUnit)}` : ''}${i.disposition === 'reject' ? ' (reject)' : ''}`)
              .join('; '),
    },
    { key: 'tech', header: 'Technician', render: (l) => (l.technicianName ? `${l.technicianName} · ${ndtLevelLabel(l.technicianLevel)}` : '—') },
    { key: 'day', header: 'Exam day', render: (l) => formatCalendarDate(l.examDay) ?? '—' },
  ];
  const rows = lines.map((l) => ({ ...l, id: l.examId }));
  return (
    <NdtPanel eyebrow="Coverage" title="Exams on this report" testId="ndt-report-lines" footer={rows.length ? `${rows.length} exam${rows.length === 1 ? '' : 's'}` : undefined}>
      <NdtRegister
        query={{ isLoading: false }}
        rows={rows}
        columns={columns}
        rail={(l) => ndtExamResultSkin(l.result).rail}
        subject="the report's exams"
        testId="ndt-report-line"
        minWidth="min-w-[1180px]"
        empty={<NdtEmpty>{report.status === 'draft' ? 'No interpreted exam falls in this report yet.' : 'This report lists no exams.'}</NdtEmpty>}
        card={(l) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-sm font-extrabold text-white">{l.weldNumber}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {l.methodLabel || l.method} · {ndtCycleLabel(l.cycle)} · {l.technicianName || '—'}
              </p>
            </div>
            <NdtBadge skin={ndtExamResultSkin(l.result)} value={l.result} />
          </div>
        )}
      />
    </NdtPanel>
  );
}

function ReferencePanels({ report, activityUnit }: { report: NdtReport; activityUnit: string | null }) {
  const h = report.header;
  if (!h) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <NdtPanel eyebrow="People" title="Technicians" testId="ndt-report-techs">
        {h.technicians?.length ? (
          <ul className="divide-y divide-fieldiq-border">
            {h.technicians.map((t) => (
              <li key={t.userId} className="px-4 py-3 text-sm text-fieldiq-text">
                <p className="font-extrabold text-white">{t.name || '—'}</p>
                {t.certs.length === 0 ? (
                  <p className="text-xs text-fieldiq-muted">No certificate cited</p>
                ) : (
                  t.certs.map((c, n) => (
                    <p key={n} className="text-xs text-fieldiq-muted">
                      {c.methodLabel || c.method} {ndtLevelLabel(c.level)} · {c.schemeLabel || c.scheme}
                      {c.certificateNumber ? ` · #${c.certificateNumber}` : ''}
                      {c.expiresOn ? ` · expires ${formatCalendarDate(c.expiresOn) ?? '—'}` : ''}
                    </p>
                  ))
                )}
              </li>
            ))}
          </ul>
        ) : (
          <NdtEmpty>No technician is cited.</NdtEmpty>
        )}
      </NdtPanel>
      <NdtPanel eyebrow="Gear" title="Instruments">
        {h.instruments?.length ? (
          <ul className="divide-y divide-fieldiq-border">
            {h.instruments.map((i) => (
              <li key={i.equipmentId} className="px-4 py-3 text-sm text-fieldiq-text">
                <p className="font-extrabold text-white">
                  {i.name || '—'}
                  {i.serialNumber ? <span className="ml-1 font-mono text-xs text-fieldiq-muted">{i.serialNumber}</span> : null}
                </p>
                <p className="text-xs text-fieldiq-muted">
                  Calibrated {formatCalendarDate(i.calibratedOn) ?? '—'} · due {formatCalendarDate(i.dueOn) ?? '—'}
                  {i.calibrationCertificate ? ` · cert ${i.calibrationCertificate}` : ''}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <NdtEmpty>No instrument is cited.</NdtEmpty>
        )}
      </NdtPanel>
      <NdtPanel eyebrow="Radiation" title="Sources">
        {h.sources?.length ? (
          <ul className="divide-y divide-fieldiq-border">
            {h.sources.map((s) => (
              <li key={s.sourceId} className="px-4 py-3 text-sm text-fieldiq-text">
                <p className="font-extrabold text-white">
                  {s.isotope || '—'} <span className="font-mono text-xs text-fieldiq-muted">{s.serialNumber || ''}</span>
                </p>
                <p className="text-xs text-fieldiq-muted">
                  Activity at exams:{' '}
                  {s.activityAtExams.length === 0
                    ? '—'
                    : s.activityAtExams[0] === s.activityAtExams[s.activityAtExams.length - 1]
                      ? formatNdtActivity(s.activityAtExams[0], activityUnit)
                      : `${formatNdtActivity(s.activityAtExams[0], null)} – ${formatNdtActivity(s.activityAtExams[s.activityAtExams.length - 1], activityUnit)}`}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <NdtEmpty>No radiation source was used.</NdtEmpty>
        )}
      </NdtPanel>
      <p className="text-xs text-fieldiq-muted lg:col-span-3">
        <FileText className="mr-1 inline size-3.5" aria-hidden="true" />
        Each certificate, calibration and source is the one the exam relied on when it was recorded.
      </p>
    </div>
  );
}
